import React, { useState, useMemo } from 'react';
import { 
  Calculator, 
  Clock, 
  DollarSign, 
  TrendingDown,
  CheckCircle2,
  FileSpreadsheet,
  Users,
  Database,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

// COMPLEXITY SECONDS PER ROW
const COMPLEXITY_OPTIONS = [
  { 
    id: 'basic', 
    seconds: 3, 
    label: 'Basic', 
    description: 'Names, dates, capitalization',
    icon: CheckCircle2,
    color: 'text-green-500'
  },
  { 
    id: 'standard', 
    seconds: 7, 
    label: 'Standard', 
    description: 'Emails, phones, countries',
    icon: FileSpreadsheet,
    color: 'text-blue-500'
  },
  { 
    id: 'advanced', 
    seconds: 15, 
    label: 'Advanced', 
    description: 'Revenue, dropdowns, gender',
    icon: Database,
    color: 'text-purple-500'
  },
];

// HOURLY RATE OPTIONS
const HOURLY_RATES = [
  { value: 15, label: '$15/hr', subtitle: 'Intern / Junior' },
  { value: 25, label: '$25/hr', subtitle: 'Admin / Sales Ops' },
  { value: 50, label: '$50/hr', subtitle: 'Senior Analyst' },
  { value: 75, label: '$75/hr', subtitle: 'Consultant' },
];

const SavingsCalculator = ({ onClose, onGetStarted }) => {
  const [rows, setRows] = useState(10000);
  const [hourlyRate, setHourlyRate] = useState(25);
  const [complexity, setComplexity] = useState('standard');

  // Calculate savings
  const calculations = useMemo(() => {
    const complexityOption = COMPLEXITY_OPTIONS.find(c => c.id === complexity);
    const secondsPerRow = complexityOption?.seconds || 7;
    
    const totalManualSeconds = rows * secondsPerRow;
    const totalManualHours = totalManualSeconds / 3600;
    const totalManualCost = totalManualHours * hourlyRate;

    return {
      manualHours: totalManualHours,
      manualCost: totalManualCost,
    };
  }, [rows, hourlyRate, complexity]);

  // Format numbers
  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatHours = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    } else if (hours < 8) {
      return `${hours.toFixed(1)} hours`;
    } else {
      const days = Math.round(hours / 8);
      return `${Math.round(hours)} hours (${days} days)`;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 border border-slate-700/50 shadow-2xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Calculator className="w-4 h-4" />
          ROI Calculator
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
          How much are you <span className="text-green-400">really</span> spending?
        </h2>
        <p className="text-slate-400">
          See how much time and money you'll save with automated data cleaning
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* INPUT SECTION */}
        <div className="space-y-6">
          {/* Rows Slider */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Number of Contacts
              </label>
              <span className="text-2xl font-bold text-white">
                {formatNumber(rows)}
              </span>
            </div>
            <Slider
              value={[rows]}
              onValueChange={(value) => setRows(value[0])}
              min={1000}
              max={100000}
              step={1000}
              className="w-full"
              data-testid="rows-slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>1,000</span>
              <span>100,000+</span>
            </div>
          </div>

          {/* Hourly Rate Dropdown */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-green-400" />
              Hourly Rate
            </label>
            <div className="grid grid-cols-2 gap-2">
              {HOURLY_RATES.map((rate) => (
                <button
                  key={rate.value}
                  onClick={() => setHourlyRate(rate.value)}
                  data-testid={`rate-${rate.value}`}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    hourlyRate === rate.value
                      ? 'border-indigo-500 bg-indigo-500/20 text-white'
                      : 'border-slate-600 bg-slate-700/30 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  <div className="font-semibold">{rate.label}</div>
                  <div className="text-xs text-slate-400">{rate.subtitle}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Complexity Selection */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-yellow-400" />
              Data Complexity
            </label>
            <div className="space-y-2">
              {COMPLEXITY_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => setComplexity(option.id)}
                    data-testid={`complexity-${option.id}`}
                    className={`w-full p-4 rounded-lg border-2 transition-all flex items-center gap-4 ${
                      complexity === option.id
                        ? 'border-indigo-500 bg-indigo-500/20'
                        : 'border-slate-600 bg-slate-700/30 hover:border-slate-500'
                    }`}
                  >
                    <div className={`p-2 rounded-lg bg-slate-700/50 ${option.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-white">{option.label}</div>
                      <div className="text-xs text-slate-400">{option.description}</div>
                    </div>
                    <div className="text-xs text-slate-500">
                      {option.seconds}s/row
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* OUTPUT SECTION */}
        <div className="space-y-4">
          {/* Manual Cost - Red */}
          <div className="bg-gradient-to-r from-red-900/30 to-red-800/20 rounded-xl p-6 border border-red-500/30">
            <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
              <TrendingDown className="w-4 h-4" />
              Manual Labor Cost
            </div>
            <div className="text-4xl md:text-5xl font-bold text-red-400 mb-2" data-testid="manual-cost">
              {formatCurrency(calculations.manualCost)}
            </div>
            <div className="text-sm text-slate-400">
              That's <span className="text-red-300 font-semibold">{formatHours(calculations.manualHours)}</span> of work at ${hourlyRate}/hr
            </div>
          </div>

          {/* Savings - Highlight */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent"></div>
            <div className="relative">
              <div className="flex items-center gap-2 text-indigo-200 text-sm font-medium mb-2">
                <Clock className="w-4 h-4" />
                Time You Get Back
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2" data-testid="savings-amount">
                {formatHours(calculations.manualHours)}
              </div>
              <div className="text-indigo-100 font-medium">
                Worth {formatCurrency(calculations.manualCost)} of manual work, done in seconds
              </div>
            </div>
          </div>

          {/* Summary Quote */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <p className="text-slate-300 text-sm leading-relaxed">
              <span className="text-white font-semibold">Bottom line:</span> Hiring someone to clean{' '}
              <span className="text-blue-400 font-semibold">{formatNumber(rows)} rows</span> costs{' '}
              <span className="text-red-400 font-semibold">{formatCurrency(calculations.manualCost)}</span> and takes{' '}
              <span className="text-yellow-400 font-semibold">{formatHours(calculations.manualHours)}</span>.
              <br />
              <span className="text-green-400 font-semibold">We do it in seconds.</span>
            </p>
          </div>

          {/* CTA Button */}
          <Button 
            onClick={onGetStarted}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-semibold py-6 text-lg rounded-xl shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
            data-testid="calculator-cta"
          >
            Start Saving Now →
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SavingsCalculator;
